import Ajv from 'ajv'
import addFormats from 'ajv-formats'
import type { SxProps, Theme } from '@mui/material/styles'
import type {
  CurrentSchema,
  JSONContent,
  JsonSchemaResult,
  EditorLayout,
} from './types'
import { camelCase } from 'change-case'

// Конвертируем CSSStyleDeclaration в объект, совместимый с Material UI sx
export function convertStylesToSx(
  styles: Partial<CSSStyleDeclaration>
): SxProps<Theme> {
  const sx: Record<string, any> = {}

  Object.entries(styles).forEach(([key, value]) => {
    if (value && typeof value === 'string') {
      // Пропускаем служебные свойства CSSStyleDeclaration
      if (
        ![
          'parentRule',
          'cssText',
          'length',
          'item',
          'getPropertyValue',
          'setProperty',
          'removeProperty',
          'getPropertyPriority',
        ].includes(key)
      ) {
        sx[key] = value
      }
    }
  })

  return sx
}

export function extractTextFromNode(node: JSONContent): string {
  if (node.type === 'text' && node.text) {
    return node.text
  }
  if (node.content) {
    return node.content.map(extractTextFromNode).join('')
  }

  return ''
}

// Разделяем content на страницы по page разделителям
// page работает как разделитель, а не как группа
// Возвращает обычные страницы и thank you page отдельно
export function splitContentByPages(content: JSONContent): {
  pages: JSONContent[][]
  hasPages: boolean
  thankYouPage: JSONContent | null
} {
  if (!content.content) {
    return { pages: [], hasPages: false, thankYouPage: null }
  }

  const pages: JSONContent[][] = []
  let currentPage: JSONContent[] = []
  let thankYouPage: JSONContent | null = null
  let foundThankYouPage = false
  const thankYouPageContent: JSONContent[] = []

  content.content.forEach((node) => {
    if (node.type === 'page') {
      const isThankYouPage = node.attrs?.isThankYouPage === true

      if (isThankYouPage) {
        // Сохраняем текущую страницу перед thank you page
        if (currentPage.length > 0) {
          pages.push(currentPage)
          currentPage = []
        }
        // Создаем копию thank you page node, чтобы не мутировать исходный объект
        thankYouPage = {
          ...node,
          content: node.content ? [...node.content] : [],
        }
        foundThankYouPage = true
        // Не добавляем thank you page в currentPage
      } else {
        // Обычный разделитель страницы - сохраняем текущую страницу и начинаем новую
        if (currentPage.length > 0) {
          pages.push(currentPage)
          currentPage = []
        }
      }
    } else {
      // Если уже нашли thank you page, весь последующий контент относится к нему
      if (foundThankYouPage) {
        // Собираем контент после thank you page
        thankYouPageContent.push(node)
      } else {
        // Добавляем ноду в текущую страницу
        currentPage.push(node)
      }
    }
  })

  // Объединяем контент из thankYouPage.content и контент после него
  if (thankYouPage && thankYouPageContent.length > 0) {
    const existingContent = (thankYouPage as JSONContent).content || []
    ;(thankYouPage as JSONContent).content = [
      ...existingContent,
      ...thankYouPageContent,
    ]
  }

  // Добавляем последнюю страницу, если она не пустая и мы не нашли thank you page
  if (currentPage.length > 0 && !foundThankYouPage) {
    pages.push(currentPage)
  }

  // Если не было page разделителей, но есть контент - это одна страница
  if (pages.length === 0 && content.content.length > 0) {
    // Проверяем, были ли page разделители
    const hasPageSeparators = content.content.some(
      (node) => node.type === 'page'
    )
    if (!hasPageSeparators) {
      return { pages: [], hasPages: false, thankYouPage: null }
    }
  }

  // Если есть только thank you page и нет обычных страниц, hasPages = false
  const hasRegularPages = pages.length > 0

  return { pages, hasPages: hasRegularPages, thankYouPage }
}

// Функция для получения имени поля по fieldId из content
// Использует ту же логику, что и RenderFieldInput: name || fieldId
export function getFieldNameByFieldId(
  content: JSONContent,
  fieldId: string,
  layout?: EditorLayout
): string | null {
  // Сначала проверяем layout.fields - это основной источник name
  if (layout?.fields?.[fieldId]?.name) {
    return layout.fields[fieldId].name
  }

  // Если нет в layout.fields, ищем в node.attrs (fallback для старых данных)
  let fieldName: string | null = null

  function traverse(node: JSONContent) {
    const fieldInputTypes = [
      'shortAnswerFieldInput',
      'longAnswerFieldInput',
      'numberFieldInput',
      'emailFieldInput',
      'phoneNumberFieldInput',
      'linkFieldInput',
      'dateFieldInput',
      'timeFieldInput',
      'dateTimeFieldInput',
      'booleanFieldInput',
      'fileUploaderFieldInput',
      'fieldChoiceOption',
      'fieldCheckboxOption',
      'fieldSelectOption',
      'fieldMultiselectOption',
    ]

    if (node.type && fieldInputTypes.includes(node.type)) {
      const attrs = node.attrs || {}
      if (attrs.fieldId === fieldId) {
        // Используем attrs.name только как fallback
        const name = attrs.name || ''
        if (name && name !== fieldId) {
          // Не возвращаем fieldId как name
          fieldName = String(name)
        }
      }
    }

    if (node.content) {
      node.content.forEach(traverse)
    }
  }

  traverse(content)

  return fieldName
}

export type ChoiceOption = {
  optionId: string
  label: string
  hidden: boolean
  value: string
}

export function getChoiceOptions(
  content: JSONContent,
  fieldId: string
): ChoiceOption[] {
  const options: ChoiceOption[] = []

  function traverse(node: JSONContent) {
    if (
      (node.type === 'fieldChoiceOption' ||
        node.type === 'fieldCheckboxOption' ||
        node.type === 'fieldSelectOption' ||
        node.type === 'fieldMultiselectOption') &&
      node.attrs?.fieldId === fieldId
    ) {
      options.push({
        optionId: node.attrs?.optionId || node.attrs?.name || '',
        label: extractTextFromNode(node) || node.attrs?.labelText || '',
        hidden: node.attrs?.hidden === true,
        value:
          node.attrs?.value || node.attrs?.optionId || node.attrs?.name || '',
      })
    }

    if (node.content) {
      node.content.forEach(traverse)
    }
  }

  traverse(content)

  return options
}

// Функция для проверки наличия fieldLabel для данного fieldId
export function hasFieldLabel(content: JSONContent, fieldId: string): boolean {
  let hasLabel = false

  function traverse(node: JSONContent) {
    if (node.type === 'fieldLabel' && node.attrs?.fieldId === fieldId) {
      hasLabel = true

      return
    }

    if (node.content) {
      node.content.forEach(traverse)
    }
  }

  traverse(content)

  return hasLabel
}

// Функции collectConditions и buildCompiledJsonSchema вынесены в editor/utils/build-validation-schema.ts

// Компилируем currentSchema на основе условий и текущих значений формы
// Обрабатываем все if/then/allOf/anyOf и мержим в JSON-schema
export function compileCurrentSchema(
  compiledJsonSchema: any,
  formValues: Record<string, any>
): CurrentSchema {
  // strict: false позволяет использовать нестандартные ключевые слова (например, meta)
  const ajv = new Ajv({ strict: false })
  addFormats(ajv as any) // Добавляем поддержку форматов (email, uri и т.д.)

  // Начинаем с базовой схемы (копируем properties и required)
  const result: any = {
    type: 'object',
    properties: { ...compiledJsonSchema.properties },
    required: compiledJsonSchema.required
      ? [...compiledJsonSchema.required]
      : [],
  }

  // Обрабатываем allOf (conditions)
  if (compiledJsonSchema.allOf && Array.isArray(compiledJsonSchema.allOf)) {
    for (const conditionSchema of compiledJsonSchema.allOf) {
      if (conditionSchema.if && conditionSchema.then) {
        // Создаем схему для проверки условия if
        const ifSchema: any = {
          type: 'object',
        }

        if (conditionSchema.if.properties) {
          ifSchema.properties = conditionSchema.if.properties
        }

        if (conditionSchema.if.allOf && conditionSchema.if.allOf.length > 0) {
          ifSchema.allOf = conditionSchema.if.allOf
        }

        if (conditionSchema.if.anyOf && conditionSchema.if.anyOf.length > 0) {
          ifSchema.anyOf = conditionSchema.if.anyOf
        }

        // Проверяем условие if с помощью Ajv
        try {
          const validate = ajv.compile(ifSchema)
          const isValid = validate(formValues)

          if (isValid && conditionSchema.then) {
            // Если условие выполнено, мержим then в result
            // Мержим required
            if (conditionSchema.then.required) {
              conditionSchema.then.required.forEach((fieldName: string) => {
                if (!result.required.includes(fieldName)) {
                  result.required.push(fieldName)
                }
              })
            }

            // Мержим properties (включая meta)
            if (conditionSchema.then.properties) {
              Object.entries(conditionSchema.then.properties).forEach(
                ([fieldName, fieldConfig]: [string, any]) => {
                  if (!result.properties[fieldName]) {
                    result.properties[fieldName] = {}
                  }
                  // Мержим fieldConfig в существующее свойство
                  result.properties[fieldName] = {
                    ...result.properties[fieldName],
                    ...fieldConfig,
                  }
                }
              )
            }
          }
        } catch (e) {
          console.error('Error validating condition:', e)
        }
      }
    }
  }

  // Обрабатываем anyOf (если есть)
  if (compiledJsonSchema.anyOf && Array.isArray(compiledJsonSchema.anyOf)) {
    for (const conditionSchema of compiledJsonSchema.anyOf) {
      if (conditionSchema.if && conditionSchema.then) {
        const ifSchema: any = {
          type: 'object',
        }

        if (conditionSchema.if.properties) {
          ifSchema.properties = conditionSchema.if.properties
        }

        if (conditionSchema.if.allOf && conditionSchema.if.allOf.length > 0) {
          ifSchema.allOf = conditionSchema.if.allOf
        }

        if (conditionSchema.if.anyOf && conditionSchema.if.anyOf.length > 0) {
          ifSchema.anyOf = conditionSchema.if.anyOf
        }

        try {
          const validate = ajv.compile(ifSchema)
          const isValid = validate(formValues)

          if (isValid && conditionSchema.then) {
            if (conditionSchema.then.required) {
              conditionSchema.then.required.forEach((fieldName: string) => {
                if (!result.required.includes(fieldName)) {
                  result.required.push(fieldName)
                }
              })
            }

            if (conditionSchema.then.properties) {
              Object.entries(conditionSchema.then.properties).forEach(
                ([fieldName, fieldConfig]: [string, any]) => {
                  if (!result.properties[fieldName]) {
                    result.properties[fieldName] = {}
                  }
                  result.properties[fieldName] = {
                    ...result.properties[fieldName],
                    ...fieldConfig,
                  }
                }
              )
            }
          }
        } catch (e) {
          console.error('Error validating condition:', e)
        }
      }
    }
  }

  // Удаляем пустой required
  if (result.required.length === 0) {
    delete result.required
  }

  return result
}

export function parseStyleString(
  styleContent: string | undefined
): Partial<CSSStyleDeclaration> {
  const result = {}
  if (!styleContent) return result

  let prop = ''
  let value = ''
  let readingProp = true
  let insideQuotes = false
  let quoteChar = ''
  let parenthesesDepth = 0

  for (let i = 0; i < styleContent.length; i++) {
    const char = styleContent[i]

    if (readingProp) {
      if (char === ':') {
        readingProp = false
      } else {
        prop += char
      }
    } else {
      // reading value
      if (insideQuotes) {
        value += char
        if (char === quoteChar) {
          insideQuotes = false
          quoteChar = ''
        }
      } else {
        if (char === '"' || char === "'") {
          insideQuotes = true
          quoteChar = char
          value += char
        } else if (char === '(') {
          parenthesesDepth++
          value += char
        } else if (char === ')') {
          parenthesesDepth--
          value += char
        } else if (char === ';' && parenthesesDepth === 0) {
          // end of this property
          const cleanProp = prop.trim()
          const cleanValue = value.trim()
          if (cleanProp) {
            //@ts-ignore
            result[camelCase(cleanProp)] = cleanValue
          }
          prop = ''
          value = ''
          readingProp = true
        } else {
          value += char
        }
      }
    }
  }

  // последний пропертя если нет в конце ;
  if (prop.trim()) {
    //@ts-ignore
    result[camelCase(prop.trim())] = value.trim()
  }

  return result as Partial<CSSStyleDeclaration>
}

// Собираем все conditions из content
export function collectConditions(content: JSONContent): JsonSchemaResult[] {
  const conditions: JsonSchemaResult[] = []

  function traverse(node: JSONContent) {
    if (node.type === 'fieldConditions') {
      const attrs = node.attrs || {}
      const jsonSchema = attrs.jsonSchema
      if (jsonSchema && typeof jsonSchema === 'string') {
        try {
          const parsed = JSON.parse(jsonSchema)
          if (parsed.if && parsed.then) {
            conditions.push(parsed as JsonSchemaResult)
          } else {
            console.warn('Condition missing if or then:', parsed)
          }
        } catch (e) {
          console.error('Failed to parse condition JSON schema:', e, jsonSchema)
        }
      } else {
        console.warn(
          'Field condition node missing jsonSchema attribute:',
          attrs
        )
      }
    }

    if (node.content) {
      node.content.forEach(traverse)
    }
  }

  traverse(content)

  return conditions
}

// Строим полную JSON-schema из атрибутов полей и conditions
export function buildCompiledJsonSchema(
  content: JSONContent,
  conditions: JsonSchemaResult[],
  layout?: EditorLayout
): any {
  const schema: any = {
    type: 'object',
    properties: {},
    required: [],
    allOf: [],
    anyOf: [],
  }

  // Собираем схему из всех полей
  const processedChoiceFieldIds = new Set<string>()
  const processedCheckboxFieldIds = new Set<string>()

  function collectFieldSchemas(node: JSONContent) {
    const fieldInputTypes = [
      'shortAnswerFieldInput',
      'longAnswerFieldInput',
      'numberFieldInput',
      'emailFieldInput',
      'phoneNumberFieldInput',
      'linkFieldInput',
      'dateFieldInput',
      'timeFieldInput',
      'dateTimeFieldInput',
      'fileUploaderFieldInput',
      'fieldChoiceOption',
      'fieldCheckboxOption',
      'fieldSelectOption',
      'fieldMultiselectOption',
    ]
    if (node.type && fieldInputTypes.includes(node.type)) {
      const attrs = node.attrs || {}
      const fieldId = attrs.fieldId || ''
      const isChoice = node.type === 'fieldChoiceOption'
      const isCheckbox = node.type === 'fieldCheckboxOption'
      const isSelect = node.type === 'fieldSelectOption'
      const isMultiselect = node.type === 'fieldMultiselectOption'

      if (
        (isChoice &&
          (fieldId === '' || processedChoiceFieldIds.has(fieldId))) ||
        (isCheckbox &&
          (fieldId === '' || processedCheckboxFieldIds.has(fieldId))) ||
        (isSelect &&
          (fieldId === '' || processedChoiceFieldIds.has(fieldId))) ||
        (isMultiselect &&
          (fieldId === '' || processedCheckboxFieldIds.has(fieldId)))
      ) {
        // Уже обработали это поле выбора/чекбоксов
      } else {
        if ((isChoice || isSelect) && fieldId) {
          processedChoiceFieldIds.add(fieldId)
        }
        if ((isCheckbox || isMultiselect) && fieldId) {
          processedCheckboxFieldIds.add(fieldId)
        }

        // Получаем настройки из layout.fields, если они есть
        const fieldSettings = fieldId && layout?.fields?.[fieldId]

        const name =
          fieldSettings?.name ||
          attrs.name ||
          fieldId ||
          `field-${fieldId || Math.random().toString(36).substr(2, 9)}`
        const required = fieldSettings?.required ?? attrs.required ?? false

        // Определяем тип поля
        let fieldType: 'string' | 'number' | 'object' | 'array' = 'string'
        if (node.type === 'numberFieldInput') {
          fieldType = 'number'
        } else if (node.type === 'fileUploaderFieldInput') {
          fieldType = 'object' // Файлы - объекты
        } else if (isCheckbox || isMultiselect) {
          fieldType = 'array'
        }

        const fieldSchema: any = { type: fieldType }

        if (fieldType === 'array') {
          fieldSchema.items = { type: 'string' }

          // Для массивов (checkboxes, multiselect) используем minItems/maxItems
          // Согласно JSON Schema спецификации: minItems и maxItems должны быть неотрицательными целыми числами
          const minItems = fieldSettings?.minItems ?? attrs.minItems
          const maxItems = fieldSettings?.maxItems ?? attrs.maxItems
          const minItemsEnabled =
            fieldSettings?.minItemsEnabled ?? attrs.minItemsEnabled ?? false
          const maxItemsEnabled =
            fieldSettings?.maxItemsEnabled ?? attrs.maxItemsEnabled ?? false

          // Если поле required, автоматически устанавливаем minItems: 1, если minItemsEnabled = false
          // или minItems не установлен
          if (required) {
            if (
              minItemsEnabled &&
              minItems !== undefined &&
              minItems !== null
            ) {
              const minItemsValue =
                typeof minItems === 'number'
                  ? minItems
                  : parseInt(String(minItems), 10)
              if (!isNaN(minItemsValue) && minItemsValue >= 0) {
                fieldSchema.minItems = minItemsValue
              } else {
                // Если значение некорректное, устанавливаем минимум 1 для required поля
                fieldSchema.minItems = 1
              }
            } else {
              // Если minItemsEnabled = false или minItems не установлен, но поле required,
              // устанавливаем minItems: 1 чтобы массив не был пустым
              fieldSchema.minItems = 1
            }
          } else if (
            minItemsEnabled &&
            minItems !== undefined &&
            minItems !== null
          ) {
            // Если поле не required, устанавливаем minItems только если minItemsEnabled = true
            const minItemsValue =
              typeof minItems === 'number'
                ? minItems
                : parseInt(String(minItems), 10)
            if (!isNaN(minItemsValue) && minItemsValue >= 0) {
              fieldSchema.minItems = minItemsValue
            }
          }

          if (maxItemsEnabled && maxItems !== undefined && maxItems !== null) {
            const maxItemsValue =
              typeof maxItems === 'number'
                ? maxItems
                : parseInt(String(maxItems), 10)
            if (!isNaN(maxItemsValue) && maxItemsValue >= 0) {
              fieldSchema.maxItems = maxItemsValue
            }
          }
        } else if (fieldType === 'string') {
          // Для строковых полей используем minLength/maxLength
          // Согласно JSON Schema спецификации: minLength и maxLength должны быть неотрицательными целыми числами
          const minLength = fieldSettings?.minLength ?? attrs.minLength
          const maxLength = fieldSettings?.maxLength ?? attrs.maxLength
          const minLengthEnabled =
            fieldSettings?.minLengthEnabled ?? attrs.minLengthEnabled ?? false
          const maxLengthEnabled =
            fieldSettings?.maxLengthEnabled ?? attrs.maxLengthEnabled ?? false

          if (
            minLengthEnabled &&
            minLength !== undefined &&
            minLength !== null
          ) {
            const minLengthValue =
              typeof minLength === 'number'
                ? minLength
                : parseInt(String(minLength), 10)
            if (!isNaN(minLengthValue) && minLengthValue >= 0) {
              fieldSchema.minLength = minLengthValue
            }
          }
          if (
            maxLengthEnabled &&
            maxLength !== undefined &&
            maxLength !== null
          ) {
            const maxLengthValue =
              typeof maxLength === 'number'
                ? maxLength
                : parseInt(String(maxLength), 10)
            if (!isNaN(maxLengthValue) && maxLengthValue >= 0) {
              fieldSchema.maxLength = maxLengthValue
            }
          }
        } else if (fieldType === 'number') {
          // Для числовых полей можно использовать minimum/maximum
          // Но пока не реализовано в UI, оставляем для будущего расширения
        }
        if (node.type === 'emailFieldInput') {
          fieldSchema.format = 'email'
        }
        if (node.type === 'linkFieldInput') {
          fieldSchema.format = 'uri'
        }

        schema.properties[name] = fieldSchema

        // Добавляем в required, если поле required
        if (required) {
          if (!schema.required.includes(name)) {
            schema.required.push(name)
          }
        }
      }
    }

    if (node.content) {
      node.content.forEach(collectFieldSchemas)
    }
  }

  if (content.content) {
    content.content.forEach(collectFieldSchemas)
  }

  // Добавляем conditions в схему
  if (conditions.length > 0) {
    console.log('buildCompiledJsonSchema: Found conditions:', conditions.length)
  }

  for (const condition of conditions) {
    // Проверяем, что условие валидно
    if (!condition.if || !condition.then) {
      console.warn(
        'buildCompiledJsonSchema: Skipping invalid condition:',
        condition
      )
      continue
    }

    // Создаем объект с if/then для каждого condition
    // Копируем весь объект if, чтобы сохранить всю структуру
    const conditionSchema: any = {
      if: JSON.parse(JSON.stringify(condition.if)), // Глубокое копирование
      then: condition.then || {},
    }

    // Если if пустой, пропускаем
    if (Object.keys(conditionSchema.if).length === 0) {
      console.warn(
        'buildCompiledJsonSchema: Skipping condition with empty if:',
        conditionSchema
      )
      continue
    }

    // Добавляем condition в allOf
    schema.allOf.push(conditionSchema)
  }

  if (schema.allOf.length > 0) {
    console.log(
      'buildCompiledJsonSchema: Added',
      schema.allOf.length,
      'conditions to allOf'
    )
  }

  // Удаляем пустые массивы
  if (schema.allOf.length === 0) {
    delete schema.allOf
  }
  if (schema.anyOf.length === 0) {
    delete schema.anyOf
  }
  if (schema.required.length === 0) {
    delete schema.required
  }

  return schema
}
